import type { RequestConfig } from "@app/config/axios/http";
import { boApi, type ApiOmpBoState } from "@src/api/bo";
import { divisionApi } from "@src/api/dictionaries/divisions";
import type { ApiDivision } from "@src/api/dictionaries/divisions/types";
import { enterpriseServices, type Enterprise } from "@src/api/dictionaries/enterprise";
import { measureApi } from "@src/api/dictionaries/measures";
import type { ApiMeasure } from "@src/api/dictionaries/measures/types";
import { stockDepotCardsApi, type ApiStockDepotCard } from "@src/api/stock/stockDepotCards";
import { stockObjsApi, type ApiStockObj, type StockObj } from "@src/api/stock/stockObjs";
import { lotsServices } from "../lots";
import type { ApiStockLot } from "../lots";
import { stockOrdersApi } from "./api.core";
import type {
    ApiStockOrder,
    ApiStockOrderContent,
    ApiStockOrderSupplyDoc,
    StockOrder,
    StockOrderContent,
    StockOrderContentExtends,
    StockOrderContentFilter,
    StockOrderExtends,
    StockOrderFilter,
} from "./types";

/** Сервисный слой — приходные ордера: заголовки, состав и связанные справочники. */
export const stockOrdersServices = {
    ...stockOrdersApi,

    /**
     * Загружает приходные ордера и при необходимости обогащает складом, поставщиком и состоянием BO.
     * @param params фильтр и include
     * @param config опции HTTP-запроса
     */
    load: async (
        params: {
            flt?: StockOrderFilter;
            include?: Array<keyof StockOrderExtends>;
        },
        config?: RequestConfig,
    ): Promise<StockOrder[]> => {
        const { flt, include } = params;
        const orders = await stockOrdersApi.load({ flt }, config);

        if (include == null) {
            return orders.map((order) => serializeOrder(order));
        }

        const includeDivision = include.includes("Division");
        const includeSupplyDivision = include.includes("SupplyDivision");
        const includeSupplyEnterprise = include.includes("SupplyEnterprise");
        const includeState = include.includes("State");
        const includeSupplyDocs = include.includes("SupplyDocs");
        const divisionCodes = new Set<number>();
        const enterpriseCodes = new Set<number>();
        const stateCodes = new Set<number>();
        const orderCodes = new Set<number>();

        orders.forEach((order) => {
            if (includeDivision) {
                addValidCode(divisionCodes, order.DivisionCode);
            }
            if (includeSupplyDivision) {
                addValidCode(divisionCodes, order.SupplyDivisionCode);
            }
            if (includeSupplyEnterprise) {
                addValidCode(enterpriseCodes, order.SupplyEnterpriseCode);
            }
            if (includeState) {
                stateCodes.add(order.Bo.State);
            }
            if (includeSupplyDocs && isValidCode(order.Code)) {
                orderCodes.add(order.Code);
            }
        });

        const divisions = new Map<number, ApiDivision>();
        const enterprises = new Map<number, Enterprise>();
        const states = new Map<number, ApiOmpBoState>();
        const supplyDocs = new Map<number, ApiStockOrderSupplyDoc[]>();
        const promises: Promise<unknown>[] = [];

        if (divisionCodes.size > 0) {
            promises.push(
                divisionApi.load({ flt: { Codes: [...divisionCodes] } }, config).then(({ items }) => {
                    items.forEach((division) => divisions.set(division.Code, division));
                }),
            );
        }

        if (enterpriseCodes.size > 0) {
            promises.push(
                enterpriseServices.load({ flt: { Codes: [...enterpriseCodes] } }, config).then((items) => {
                    items.forEach((enterprise) => enterprises.set(enterprise.Code, enterprise));
                }),
            );
        }

        if (stateCodes.size > 0) {
            promises.push(
                boApi.states.load({ Codes: [...stateCodes] }, config).then((items) => {
                    items.forEach((state) => states.set(state.Code, state));
                }),
            );
        }

        if (orderCodes.size > 0) {
            promises.push(
                stockOrdersApi.loadSupplyDocs({ stockOrders: [...orderCodes] }, config).then((items) => {
                    items.forEach((doc) => {
                        if (!isValidCode(doc.StockOrderCode)) {
                            return;
                        }

                        const docs = supplyDocs.get(doc.StockOrderCode) ?? [];
                        docs.push(doc);
                        supplyDocs.set(doc.StockOrderCode, docs);
                    });
                }),
            );
        }

        await Promise.all(promises);

        return orders.map((order) =>
            serializeOrder(order, {
                Division: includeDivision && isValidCode(order.DivisionCode) ? divisions.get(order.DivisionCode) : undefined,
                SupplyDivision:
                    includeSupplyDivision && isValidCode(order.SupplyDivisionCode)
                        ? divisions.get(order.SupplyDivisionCode)
                        : undefined,
                SupplyEnterprise:
                    includeSupplyEnterprise && isValidCode(order.SupplyEnterpriseCode)
                        ? enterprises.get(order.SupplyEnterpriseCode)
                        : undefined,
                State: includeState ? states.get(order.Bo.State) : undefined,
                SupplyDocs: includeSupplyDocs ? (supplyDocs.get(order.Code) ?? []) : undefined,
            }),
        );
    },

    /**
     * Возвращает один приходный ордер по фильтру.
     * @param params фильтр и include
     * @param config опции HTTP-запроса
     */
    loadByCode: async (
        params: {
            flt: StockOrderFilter;
            include?: Array<keyof StockOrderExtends>;
        },
        config?: RequestConfig,
    ): Promise<StockOrder | null> => {
        const orders = await stockOrdersServices.load(params, config);
        return orders[0] ?? null;
    },

    content: {
        /**
         * Загружает состав приходного ордера и догружает складские карточки, ТМЦ, меры и партии.
         * @param params фильтр и include состава
         * @param config опции HTTP-запроса
         */
        load: async (
            params: {
                flt?: StockOrderContentFilter;
                include?: Array<keyof StockOrderContentExtends>;
            },
            config?: RequestConfig,
        ): Promise<StockOrderContent[]> => {
            const { flt, include } = params;
            const contents = await stockOrdersApi.content.load({ flt }, config);

            if (include == null) {
                return contents.map((content) => serializeContent(content));
            }

            const includeStockObj = include.includes("StockObj");
            const includeMeasure = include.includes("Measure");
            const includeLot = include.includes("Lot");
            const includeDepotCard = include.includes("DepotCard");
            const stockObjCodes = new Set<number>();
            const measureCodes = new Set<number>();
            const lotCodes = new Set<number>();
            const depotCodes = new Set<number>();

            contents.forEach((content) => {
                if (includeDepotCard && isValidCode(content.DepotCode)) {
                    depotCodes.add(content.DepotCode);
                }
                if (includeStockObj && isValidCode(content.StockObjCode)) {
                    stockObjCodes.add(content.StockObjCode);
                }
                if (includeMeasure && isValidCode(content.MeasCode)) {
                    measureCodes.add(content.MeasCode);
                }
                if (includeLot && isValidCode(content.LotCode)) {
                    lotCodes.add(content.LotCode);
                }
            });

            const stockObjs = new Map<number, StockObj>();
            const measures = new Map<number, ApiMeasure>();
            const lots = new Map<number, ApiStockLot>();
            const depotCards = new Map<number, ApiStockDepotCard>();
            const promises: Promise<unknown>[] = [];

            if (depotCodes.size > 0) {
                promises.push(
                    stockDepotCardsApi.load({ flt: { Codes: [...depotCodes] } }, config).then((items) => {
                        items.forEach((item) => depotCards.set(item.Code, item));
                    }),
                );
            }

            if (stockObjCodes.size > 0) {
                promises.push(
                    stockObjsApi.load({ flt: { Codes: [...stockObjCodes] } }, config).then((items) => {
                        items.forEach((item) => stockObjs.set(item.Code, serializeStockObj(item)));
                    }),
                );
            }

            if (measureCodes.size > 0) {
                promises.push(
                    measureApi.load({ flt: { Codes: [...measureCodes] } }, config).then((items) => {
                        items.forEach((item) => measures.set(item.Code, item));
                    }),
                );
            }

            if (lotCodes.size > 0) {
                promises.push(
                    lotsServices.load({ flt: { Codes: [...lotCodes] } }, config).then((items) => {
                        items.forEach((item) => lots.set(item.LotCode, item));
                    }),
                );
            }

            await Promise.all(promises);

            return contents.map((content) =>
                serializeContent(content, {
                    DepotCard:
                        includeDepotCard && isValidCode(content.DepotCode) ? depotCards.get(content.DepotCode) : undefined,
                    StockObj:
                        includeStockObj && isValidCode(content.StockObjCode)
                            ? stockObjs.get(content.StockObjCode)
                            : undefined,
                    Measure: includeMeasure && isValidCode(content.MeasCode) ? measures.get(content.MeasCode) : undefined,
                    Lot: includeLot && isValidCode(content.LotCode) ? lots.get(content.LotCode) : undefined,
                }),
            );
        },
    },
} as const;

function serializeOrder(apiOrder: ApiStockOrder, includeData?: Partial<StockOrderExtends>): StockOrder {
    return {
        ...apiOrder,
        Division: includeData?.Division,
        SupplyDivision: includeData?.SupplyDivision,
        SupplyEnterprise: includeData?.SupplyEnterprise,
        State: includeData?.State,
        SupplyDocs: includeData?.SupplyDocs,
    };
}

function serializeContent(
    apiContent: ApiStockOrderContent,
    includeData?: Partial<StockOrderContentExtends>,
): StockOrderContent {
    return {
        ...apiContent,
        DepotCard: includeData?.DepotCard,
        StockObj: includeData?.StockObj,
        Measure: includeData?.Measure,
        Lot: includeData?.Lot,
    };
}

function serializeStockObj(apiStockObj: ApiStockObj): StockObj {
    return {
        ...apiStockObj,
        KoTypeObj: undefined,
    };
}

function addValidCode(target: Set<number>, code: number): void {
    if (isValidCode(code)) {
        target.add(code);
    }
}

function isValidCode(code: number): boolean {
    return code !== 0;
}
